import type { QpxMetadataItem } from '../metadata';

export interface Measurement {
    containerWidth: number;
    gap: number;
    itemWidths: ReadonlyMap<string, number>;
    infoTriggerWidth: number;
    maxLines: number;
}

export interface WrappedLinesOptions {
    itemWidths: readonly number[];
    gap: number;
    trailingWidth: number | null;
    containerWidth: number;
}

export function getHiddenItemIds(
    items: readonly QpxMetadataItem[],
    measurement: Measurement
): readonly string[] {
    const inlineItems = items.filter((item) => !item.overflowOnly);
    const forced = items
        .filter((item) => item.overflowOnly)
        .map((item) => item.id);
    if (items.length === 0 || measurement.containerWidth <= 0) return forced;

    const allowedLines = Math.max(measurement.maxLines, 1);

    for (
        let visibleCount = inlineItems.length;
        visibleCount >= 0;
        visibleCount--
    ) {
        const visibleWidths = inlineItems
            .slice(0, visibleCount)
            .map((item) => measurement.itemWidths.get(item.id) ?? 0);
        const linesNeeded = countWrappedLines({
            itemWidths: visibleWidths,
            gap: measurement.gap,
            trailingWidth:
                visibleCount < inlineItems.length || forced.length > 0
                    ? measurement.infoTriggerWidth
                    : null,
            containerWidth: measurement.containerWidth,
        });

        if (linesNeeded <= allowedLines) {
            const visible = new Set(
                inlineItems.slice(0, visibleCount).map((item) => item.id)
            );
            return items
                .filter((item) => !visible.has(item.id))
                .map((item) => item.id);
        }
    }

    return [];
}

export function countWrappedLines(options: WrappedLinesOptions): number {
    let lines = 1;
    let currentLineWidth = 0;

    const placeWidth = (width: number): void => {
        if (currentLineWidth === 0) {
            currentLineWidth = width;
            return;
        }

        const nextWidth = currentLineWidth + options.gap + width;
        if (nextWidth <= options.containerWidth) {
            currentLineWidth = nextWidth;
            return;
        }

        lines += 1;
        currentLineWidth = width;
    };

    for (const width of options.itemWidths) {
        placeWidth(width);
    }

    if (options.trailingWidth !== null) {
        placeWidth(options.trailingWidth);
    }

    return lines;
}
